import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/layout/card';
import { Button } from '@/components/ui/button';

/**
 * Static weekly plan preview card. Plan_week + preview responses must be rendered
 * only as this card, never as a chat bubble. Deterministic, non-conversational,
 * explicit approval gate. No AI language, no animations.
 */
interface WeeklyPlanPreviewCardProps {
  /** Full preview text from backend (plan_week.message). Starts with "Preview: Week of ..." or short form. */
  previewText: string;
  onApply?: () => void;
  className?: string;
}

/** One day line: day label (e.g. "Mon") and session text, optionally split into primary + notes. */
export interface DayLine {
  dayLabel: string;
  primary: string;
  secondary: string | null;
}

interface ParsedPreview {
  title: string;
  weekRange: string | null;
  dayLines: DayLine[];
  validationBullets: string[];
  isShortForm: boolean;
}

/**
 * Splits old-style run-on session text "Title: 16.9 miles Aerobic with Recovery jog…"
 * into primary (title + distance/intensity) and secondary (notes after " with ").
 * New backend format "2 x 15-min Threshold · 16.9mi" has no " with ", so secondary stays null.
 */
function splitSessionDisplay(sessionPart: string): { primary: string; secondary: string | null } {
  const withIndex = sessionPart.indexOf(' with ');
  if (withIndex === -1) {
    return { primary: sessionPart.trim(), secondary: null };
  }
  const primary = sessionPart.slice(0, withIndex).trim();
  const secondary = sessionPart.slice(withIndex + 6).trim();
  return { primary, secondary: secondary || null };
}

function parsePreviewText(text: string): ParsedPreview {
  const trimmed = text.trim();
  const lines = trimmed.split(/\n/).map((l) => l.trim()).filter(Boolean);

  if (trimmed.startsWith('Preview: Week of ')) {
    const rawRange = lines[0]?.replace(/^Preview: Week of /, '') ?? null;
    const weekRange = rawRange ? rawRange.replace('-', ' – ') : null;
    const dayLines: DayLine[] = [];
    const validationBullets: string[] = [];
    let inQuickChecks = false;

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (line === 'Quick checks') {
        inQuickChecks = true;
        continue;
      }
      if (inQuickChecks) {
        if (line.startsWith('- ')) {
          validationBullets.push(line.slice(2));
        } else if (
          line.toLowerCase().includes('apply this plan') ||
          line.toLowerCase().includes('replace your current plan')
        ) {
          break;
        }
      } else if (line.startsWith('• ')) {
        const rest = line.slice(2);
        const dayMatch = rest.match(/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+[A-Za-z]+\s+\d+:\s*(.*)$/);
        if (dayMatch) {
          const dayLabel = dayMatch[1];
          const sessionPart = dayMatch[2];
          const { primary, secondary } = splitSessionDisplay(sessionPart);
          dayLines.push({ dayLabel, primary, secondary });
        } else {
          dayLines.push({ dayLabel: '', primary: rest, secondary: null });
        }
      }
    }

    return {
      title: 'Weekly Training Plan',
      weekRange,
      dayLines,
      validationBullets,
      isShortForm: false,
    };
  }

  return {
    title: 'Weekly Training Plan',
    weekRange: null,
    dayLines: [],
    validationBullets: [],
    isShortForm: true,
  };
}

export function WeeklyPlanPreviewCard({
  previewText,
  onApply,
  className,
}: WeeklyPlanPreviewCardProps) {
  const parsed = parsePreviewText(previewText);

  return (
    <Card className={cn('bg-accent/5 border-accent/20', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">{parsed.title}</CardTitle>
        {parsed.weekRange ? (
          <p className="text-sm text-muted-foreground font-normal">
            {parsed.weekRange}
          </p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        {parsed.dayLines.length > 0 ? (
          <div className="text-sm text-foreground space-y-2">
            {parsed.dayLines.map((line, i) => (
              <div key={i} className="leading-relaxed">
                <p>
                  {line.dayLabel ? `${line.dayLabel} • ${line.primary}` : line.primary}
                </p>
                {line.secondary ? (
                  <p className="text-muted-foreground text-xs mt-0.5 pl-0">
                    {line.secondary}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        ) : parsed.isShortForm ? (
          <div className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
            {previewText.trim()}
          </div>
        ) : null}

        {parsed.validationBullets.length > 0 ? (
          <ul className="space-y-1 text-sm text-foreground border-t border-border/50 pt-3" role="list">
            {parsed.validationBullets.map((bullet, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="text-load-fresh shrink-0" aria-hidden="true">
                  ✓
                </span>
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        ) : null}

        <p className="text-sm text-muted-foreground">
          Nothing is added until you approve.
        </p>

        {onApply ? (
          <Button
            onClick={onApply}
            className="w-full bg-coach hover:bg-coach/90 text-coach-foreground"
          >
            Apply Plan to Calendar
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
